import { AccountCircleOutlined, ArrowBack } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../../../Hooks/useAdmin";
import { useLogin } from "../../../Hooks/useLogin";
import { useMobileView } from "../../../Hooks/useMobileView";
import { useGetJobsByStatusQuery } from "../../../Redux/api/jobApi";
import { resetPrevLocation } from "../../../Redux/slices/GlobalSlice";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { NotificationsHamburger } from "./NotificationsHamburger";
import { SettingsHamburger } from "./SettingsHamburger";
import { VerticalHamburger } from "./VerticalHamburger";

export interface VerticalNavbarItem {
  label: string;
  link: string;
  disabled: boolean;
  action?: () => void;
  showBadge?: boolean;
}

export const VerticalNavbar = () => {
  /* ******************** Hooks ******************** */
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const globalSlice = useAppSelector((state) => state.Global);
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down("lg"));
  const { isMobileView } = useMobileView();
  const { isAdmin } = useAdmin();
  const { logout } = useLogin();
  const { data: jobs } = useGetJobsByStatusQuery(
    (globalSlice.userId && {
      locator: globalSlice.userId!,
      statuses: "open",
    }) ||
      skipToken
  );
  /* ******************** Variables ******************** */
  const isClient = globalSlice.userType === "client";
  const prevLocation = useAppSelector((state) => state.Global.prevLocation);
  const showSettings = useMemo(() => {
    if (window.location.pathname.includes("settings")) return true;
    return false;
  }, [window.location.pathname]);

  const getSettings = (): VerticalNavbarItem[] => {
    const settings = [
      { label: "Account info", link: "settings/accountinfo", disabled: false },
      { label: "Password", link: "settings/password", disabled: false },
      {
        label: "Notifications",
        link: "settings/notifications",
        disabled: false,
      },
      { label: "Policies", link: "settings/policies/privacy", disabled: false },
      { label: "Feedback", link: "settings/feedback", disabled: true },
      { label: "Sign out", link: "", disabled: false, action: logout },
    ];

    if (isClient)
      settings.splice(2, 0, {
        label: "Membership",
        link: "settings/membership?mode=view",
        disabled: false,
      });
    return settings;
  };
  const getAdminOptions = (): VerticalNavbarItem[] => {
    return [
      { label: "User Management", link: "admin/manage/users", disabled: false },
      { label: "Statistics", link: "admin/manage/statistics", disabled: false },
      {
        label: "Sign out",
        link: "admin/login",
        disabled: false,
        action: logout,
      },
    ];
  };

  const pages: VerticalNavbarItem[] = useMemo(() => {
    if (window.location.pathname.includes("/admin")) {
      return getAdminOptions();
    }
    if (showSettings && !isMobileView) {
      return getSettings();
    }

    const options = [
      { label: "Dashboard", link: "dashboard", disabled: false },
      { label: "Messages", link: "messages", disabled: false },
      { label: "Marketplace", link: "marketplace", disabled: false },
      { label: "Wallet", link: "wallet", disabled: !isClient },
    ];

    if (globalSlice.userType === "talent") {
      return options.concat([
        { label: "Applications", link: "applications", disabled: false },
        { label: "Bookmarked jobs", link: "bookmarks", disabled: false },
        { label: "Active jobs", link: "active-jobs", disabled: false },
        { label: "Learning", link: "#", disabled: true },
      ]);
    }

    return options.concat([
      { label: "Job postings", link: "job-postings", disabled: false },
      { label: "Bookmarked Talent", link: "bookmarks", disabled: false },
      { label: "Active jobs", link: "active-jobs", disabled: false },
    ]);
  }, [showSettings]);
  /* ******************** Functions ******************** */
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  return (
    <>
      {match ? (
        <VerticalHamburger
          mode={theme.palette.mode}
          pages={pages}
          getSettings={getSettings}
        />
      ) : (
        <Grid width={"20%"}>
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              backgroundColor: "background.secondary",
              borderRight:
                theme.palette.mode === "dark" ? "1px solid white" : "none",
            }}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              marginTop={2}
              width={"100%"}
            >
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"flex-start"}
                marginLeft={2}
              >
                <Link href="/">
                  {match ? (
                    <img
                      src="/LogoSymbolHiRes.png"
                      width={35}
                      height={"auto"}
                    />
                  ) : (
                    <img
                      src="/KaziLogoTransparent.png"
                      width={144}
                      height={"auto"}
                    />
                  )}
                </Link>
              </Box>
              {!isAdmin && (
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  flex={1}
                  marginRight={"5%"}
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                >
                  <IconButton
                    onClick={() => navigate(`/profile/${globalSlice.userType}`)}
                  >
                    <AccountCircleOutlined
                      color="primary"
                      sx={{ fontSize: "18px" }}
                    />
                  </IconButton>
                  <SettingsHamburger
                    mode={theme.palette.mode}
                    pages={getSettings()}
                  />
                  <NotificationsHamburger />
                  {/* <IconButton href="https://kazi-konekt.canny.io/feature-requests" target="_blank">
                                        <ChatBubbleOutlineOutlined color="primary" sx={{fontSize: '18px'}}/>
                                    </IconButton> */}
                </Box>
              )}
            </Box>
            <Box width={"100%"} marginTop={"40px"} marginLeft={4}>
              {showSettings && (
                <IconButton
                  onClick={() => {
                    navigate(prevLocation);
                    dispatch(resetPrevLocation());
                  }}
                >
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <ArrowBack fontSize="small" />
                    <Typography variant="label-large" color={"primary"}>
                      Back
                    </Typography>
                  </Box>
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                marginLeft: 8,
              }}
            >
              {pages.map((page, idx) => (
                <Box key={idx} marginBottom={1} marginRight={7}>
                  <MenuItem
                    selected={window.location.pathname.includes(page.link)}
                    disabled={page.disabled}
                    onClick={() => {
                      if (page.action) page.action();
                      if (page.link.length > 0) navigate(`/${page.link}`);
                    }}
                    sx={{
                      padding: 2,
                      borderRadius: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    <Typography textAlign="center" variant="title-medium">
                      {page.label}
                    </Typography>
                  </MenuItem>
                </Box>
              ))}
              {/* <ThemeToggle /> */}
            </Box>
          </Toolbar>
        </Grid>
      )}
    </>
  );
};
